/**
 * LA PORTE — où l'on envoie quelqu'un qui doit s'identifier.
 *
 * ─── Pourquoi l'application n'a plus d'écran de connexion ───────────────────
 *
 * Tout ce qui touche au trousseau et aux sessions appartient au composant de
 * management, et à lui seul. L'application ne lit pas le trousseau, ne le
 * remplit pas, ne le vide pas : elle envoie à la porte et attend le retour.
 *
 * Trois copies du même écran vivaient dans trois applications ; elles avaient
 * déjà divergé. Il n'en reste aucune.
 *
 * ─── Pourquoi une NAVIGATION, et pas un cadre dans la page ──────────────────
 *
 * Le trousseau est un cookie attaché à l'origine EXACTE de son gardien. Une
 * page servie ailleurs ne le verrait jamais — et une application hébergée sur
 * un autre domaine (un client qui garde le sien) n'en recevrait rien, avec un
 * échec SILENCIEUX pour seul symptôme. L'aller-retour, lui, marche quel que
 * soit le domaine.
 */

/**
 * L'origine du gardien du trousseau — LA MÊME variable que Sign et le kiosque.
 *
 * Un seul concept, un seul nom dans tout le parc : le jour où le gardien change
 * de domaine, c'est une valeur à changer, pas trois. Vide, on retombe sur notre
 * propre origine — le backoffice est aujourd'hui servi par le gardien lui-même.
 */
const ORIGINE_PORTE = (import.meta.env.VITE_BETOOL_IDENTITE_BFF ?? '').replace(/\/+$/, '')

/** Le `client_id` de CETTE application dans le parc. */
const APPLICATION = 'backoffice'

/**
 * LE GARDE-FOU CONTRE L'ALLER-RETOUR SANS FIN.
 *
 * Quand la porte connecte quelqu'un mais que l'application n'arrive pas à
 * ouvrir sa session, chacun fait son travail correctement : la porte renvoie
 * vers l'application, l'application constate qu'elle n'a pas de session et
 * renvoie à la porte. L'écran clignote, rien ne s'affiche, et AUCUN message
 * n'apparaît nulle part. Vécu en production : le seul symptôme était « ça
 * rafraîchit en boucle ».
 *
 * ─── POURQUOI UNE FENÊTRE DE TEMPS, ET PAS UN SIMPLE COMPTEUR ───────────────
 *
 * Un compteur nu bloquait des gens qui n'avaient AUCUN problème : se connecter,
 * travailler, se déconnecter, se reconnecter, c'est déjà deux passages par la
 * porte dans le même onglet — et le troisième était refusé, avec un message
 * d'erreur parfaitement faux. Mesuré sur le backoffice, sur un parcours normal.
 *
 * Une boucle se reconnaît à sa VITESSE : les tours s'enchaînent en une seconde,
 * sans que personne ait le temps de cliquer. Deux départs espacés de plus de
 * DELAI_BOUCLE_MS ne sont donc pas une boucle, et le compteur repart à un.
 */
const CLE_TOURS = 'betool.porte.tours'
const TOURS_MAX = 3
/* Large pour un cycle machine (qui boucle en moins d'une seconde), court pour
   un humain : personne ne se déconnecte puis se reconnecte en huit secondes. */
const DELAI_BOUCLE_MS = 8_000

type Tours = { n: number; t: number }

function lireTours(): Tours {
  try {
    const brut = window.sessionStorage.getItem(CLE_TOURS)
    if (brut === null) return { n: 0, t: 0 }
    const lu = JSON.parse(brut) as Partial<Tours>
    const n = typeof lu.n === 'number' && Number.isFinite(lu.n) && lu.n > 0 ? lu.n : 0
    const t = typeof lu.t === 'number' && Number.isFinite(lu.t) ? lu.t : 0
    return { n, t }
  } catch {
    // Stockage refusé (navigation privée stricte) ou contenu illisible : on
    // perd le garde-fou, jamais la navigation — sinon on ne pourrait plus se
    // connecter du tout.
    return { n: 0, t: 0 }
  }
}

function ecrireTours(tours: Tours): void {
  try {
    window.sessionStorage.setItem(CLE_TOURS, JSON.stringify(tours))
  } catch {
    /* voir lireTours : le garde-fou est un confort, pas une condition. */
  }
}

/**
 * À appeler dès que l'application a VRAIMENT ouvert sa session — la seule
 * preuve qu'un tour a abouti.
 */
export function entreeReussie(): void {
  try {
    window.sessionStorage.removeItem(CLE_TOURS)
  } catch {
    /* idem */
  }
}

/**
 * Vrai quand les départs se sont enchaînés TROP VITE pour qu'un humain les ait
 * provoqués, et assez souvent pour que ce ne soit pas un hasard.
 */
export function boucleDetectee(): boolean {
  const { n, t } = lireTours()
  return n >= TOURS_MAX && Date.now() - t < DELAI_BOUCLE_MS
}

/** Enregistre un départ, en repartant de un si le précédent est ancien. */
function noterDepart(): void {
  const { n, t } = lireTours()
  const maintenant = Date.now()
  const enchaine = t !== 0 && maintenant - t < DELAI_BOUCLE_MS
  ecrireTours({ n: enchaine ? n + 1 : 1, t: maintenant })
}

/**
 * L'adresse de la porte, pour cette application et ce retour.
 *
 * Le retour est notre ORIGINE, jamais un chemin : le gardien le compare par
 * égalité exacte aux points d'entrée enregistrés, et c'est ce contrôle qui
 * empêche d'envoyer un laissez-passer ailleurs. La destination interne, elle,
 * se retrouve sans le serveur — elle n'a aucune raison de traverser.
 */
export function adressePorte(options: { ajout?: boolean; deconnexion?: string } = {}): string {
  const base = ORIGINE_PORTE === '' ? window.location.origin : ORIGINE_PORTE
  const parametres = new URLSearchParams({
    app: APPLICATION,
    retour: window.location.origin,
  })
  // « Ajouter un compte » vise une identité NOUVELLE : lui répondre par la
  // liste des comptes déjà connus serait le contraire du geste demandé.
  if (options.ajout === true) parametres.set('ajout', '1')

  const adresse = `${base}/portail/choisir?${parametres.toString()}`
  if (options.deconnexion === undefined || options.deconnexion === '') return adresse

  /*
   * L'INTENTION DE FERMETURE VOYAGE DANS LE FRAGMENT.
   *
   * Le trousseau est un cookie `__Host-` : il est attaché à l'origine EXACTE
   * de son gardien, et cette application n'est pas servie là. Son « Se
   * déconnecter » ne peut donc RIEN y écrire — l'appel partait vers notre
   * propre origine et recevait 401, la session mutuelle restait ouverte, et le
   * compte se réaffichait « Connectée » juste après qu'on l'ait quitté. Mesuré
   * en production, et c'est le contraire du geste demandé.
   *
   * La fermeture s'exécute donc chez le gardien, au cours de l'aller-retour qui
   * a lieu de toute façon. Dans le FRAGMENT : ce qui suit un `#` ne quitte
   * jamais le navigateur, là où la requête mettrait l'adresse de la personne
   * dans les journaux d'accès du serveur, du proxy, et dans le `Referer`.
   */
  return `${adresse}#deconnexion=${encodeURIComponent(options.deconnexion)}`
}

/**
 * Envoie à la porte. `replace` et non `assign` : l'écran qu'on quitte n'a pas
 * à rester dans l'historique — le bouton « précédent » y ramènerait, sur une
 * page qui renverrait aussitôt à la porte.
 *
 * Rend `false` quand le garde-fou refuse de repartir : l'appelant doit alors
 * AFFICHER quelque chose. Rendre `void` laisserait le refus muet, c'est-à-dire
 * le même écran vide que la boucle qu'on corrige.
 */
export function allerALaPorte(options: { ajout?: boolean } = {}): boolean {
  if (boucleDetectee()) return false
  noterDepart()
  window.location.replace(adressePorte(options))
  return true
}

/**
 * ANNONCE QUE LA PORTE EST INJOIGNABLE, sans page ni route.
 *
 * `allerALaPorte()` rend `false` dans deux cas : porte non configurée sur cette
 * instance, ou garde-fou anti-boucle qui refuse un énième tour. Il faut alors
 * afficher quelque chose — se taire donne exactement la page blanche sans cause
 * que le garde-fou est censé rendre lisible.
 *
 * Écrit DIRECTEMENT dans le point de montage plutôt que par une route : cette
 * application n'a plus d'écran de connexion, et en réintroduire un « juste pour
 * l'erreur » ramènerait la page trompeuse qu'on vient de supprimer. Le texte
 * est constant et posé en `textContent` — rien d'externe n'y entre.
 */
export function annoncerPorteInjoignable(): void {
  const hote = document.getElementById('app')
  if (hote === null) return
  hote.textContent = ''
  const bloc = document.createElement('main')
  bloc.setAttribute('role', 'alert')
  bloc.style.cssText =
    'max-width:34rem;margin:4rem auto;padding:0 1rem;font-family:system-ui,sans-serif;line-height:1.6'
  const titre = document.createElement('h1')
  titre.style.cssText = 'font-size:1.25rem;margin:0 0 .5rem'
  titre.textContent = 'Connexion beTool indisponible'
  const texte = document.createElement('p')
  texte.textContent =
    "La page de connexion n'est pas joignable depuis cette application. Réessayez dans un instant."
  bloc.append(titre, texte)
  hote.append(bloc)
}
